import React from 'react'
import styled from 'styled-components'
import { emailService, cohortsService } from '../services/apiService'
import { useAuth } from '../context/AuthContext'
import ResponsiveLayout from '../components/ResponsiveLayout'

const EmailPanelContainer = styled.div`
  width: 100%;
`

const PanelTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 1.5rem;

  @media (min-width: 640px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`

const WorkflowContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 2fr;
    gap: 2rem;
  }
`

const EmailListPanel = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
`

const WorkflowPanel = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  min-height: 600px;
`

const EmailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 600px;
  overflow-y: auto;
`

const EmailItem = styled.div`
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? '#eff6ff' : 'white'};
  border-color: ${props => props.selected ? '#2563eb' : '#e5e7eb'};

  &:hover {
    border-color: #2563eb;
    background: #f8fafc;
  }
`

const EmailSubject = styled.h3`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #111827;
  font-size: 0.95rem;
`

const EmailSender = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`

const EmailDate = styled.p`
  color: #9ca3af;
  font-size: 0.75rem;
`

const WorkflowStep = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`

const StepTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const StepNumber = styled.span`
  background: #2563eb;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
`

const EmailContent = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  white-space: pre-wrap;
`

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 44px;

  &.primary {
    background-color: #2563eb;
    color: white;

    &:hover:not(:disabled) {
      background-color: #1d4ed8;
    }

    &:disabled {
      background-color: #9ca3af;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background-color: #6b7280;
    color: white;

    &:hover {
      background-color: #4b5563;
    }
  }

  &.outline {
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;

    &:hover {
      background-color: #f9fafb;
    }
  }

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`

const CohortGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const CohortCard = styled.div`
  padding: 1rem;
  border: 1px solid ${props => props.selected ? '#2563eb' : '#e5e7eb'};
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? '#eff6ff' : 'white'};

  &:hover {
    border-color: #2563eb;
    background: #f8fafc;
  }
`

const CohortName = styled.h4`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
`

const CohortStats = styled.p`
  color: #6b7280;
  font-size: 0.75rem;
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  resize: vertical;
  min-height: 200px;
  line-height: 1.6;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #6b7280;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`

const WorkflowState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`

const SignatureInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #0369a1;

  &.warning {
    background: #fef3c7;
    border-color: #fcd34d;
    color: #92400e;
  }
`

function EmailPanel() {
  const [emails, setEmails] = React.useState([])
  const [cohorts, setCohorts] = React.useState([])
  const [selectedEmail, setSelectedEmail] = React.useState(null)
  const [selectedCohort, setSelectedCohort] = React.useState(null)
  const [aiDraft, setAiDraft] = React.useState('')
  const [workflowStep, setWorkflowStep] = React.useState('select-email') // select-email → draft-reply → select-cohort → review-send
  const [loading, setLoading] = React.useState(true)
  const [generatingDraft, setGeneratingDraft] = React.useState(false)
  const [signatureStatus, setSignatureStatus] = React.useState('checking') // checking, found, not-found
  const { user } = useAuth()

  // Fetch emails and cohorts
  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        console.log('📧 Fetching emails and cohorts...')
        const [emailsData, cohortsResponse] = await Promise.all([
          emailService.getEmails(),
          cohortsService.getCohorts()
        ])
        
        setEmails(emailsData)
        setCohorts(cohortsResponse.cohorts || [])
        console.log(`✅ Loaded ${emailsData.length} emails and ${cohortsResponse.cohorts?.length || 0} cohorts`)
        
      } catch (error) {
        console.error('❌ Failed to fetch data:', error)
        setEmails([])
        setCohorts([])
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleEmailSelect = (email) => {
    setSelectedEmail(email)
    setWorkflowStep('draft-reply')
    setAiDraft('')
    setSelectedCohort(null)
    setSignatureStatus('checking')
  }

  const handleDraftReply = async () => {
    if (cohorts.length === 1) {
      // Auto-select if only one cohort
      setSelectedCohort(cohorts[0])
      await generateAIDraft(cohorts[0].id)
    } else {
      setWorkflowStep('select-cohort')
    }
  }

  const generateAIDraft = async (cohortId) => {
    setGeneratingDraft(true)
    try {
      console.log(`🤖 Generating AI draft for email ${selectedEmail.id} with cohort ${cohortId}`)
      const result = await emailService.generateDraft(selectedEmail.id, cohortId)
      setAiDraft(result.ai_draft)
      setWorkflowStep('review-send')
      
      // Check if signature will be added
      try {
        // This would call a new endpoint to check signature status
        // For now, we'll assume signature will be added
        setSignatureStatus('found')
      } catch (e) {
        setSignatureStatus('not-found')
      }
      
      console.log('✅ AI draft generated successfully')
    } catch (error) {
      console.error('❌ Failed to generate AI draft:', error)
      alert('Failed to generate AI draft. Please try again.')
    } finally {
      setGeneratingDraft(false)
    }
  }

  const handleCohortSelect = (cohort) => {
    setSelectedCohort(cohort)
    generateAIDraft(cohort.id)
  }

  const sendEmail = async () => {
    if (!selectedEmail || !aiDraft) {
      alert('Please generate an AI draft first')
      return
    }

    try {
      await emailService.sendEmail(selectedEmail.id, aiDraft)
      alert('Email sent successfully! Your Outlook signature has been automatically added.')
      resetWorkflow()
    } catch (error) {
      console.error('❌ Failed to send email:', error)
      alert('Failed to send email. Please try again.')
    }
  }

  const saveDraft = async () => {
    if (!selectedEmail || !aiDraft) {
      alert('Please generate an AI draft first')
      return
    }

    try {
      await emailService.saveDraft(selectedEmail.id, aiDraft)
      alert('Draft saved successfully! Your Outlook signature has been automatically added.')
    } catch (error) {
      console.error('❌ Failed to save draft:', error)
      alert('Failed to save draft. Please try again.')
    }
  }

  const resetWorkflow = () => {
    setSelectedEmail(null)
    setSelectedCohort(null)
    setAiDraft('')
    setWorkflowStep('select-email')
    setSignatureStatus('checking')
  }

  if (loading) {
    return (
      <ResponsiveLayout>
        <EmailPanelContainer>
          <PanelTitle>Email Management</PanelTitle>
          <LoadingState>Loading your emails...</LoadingState>
        </EmailPanelContainer>
      </ResponsiveLayout>
    )
  }

  return (
    <ResponsiveLayout>
      <EmailPanelContainer>
        <PanelTitle>Email Management</PanelTitle>
        
        <WorkflowContainer>
          {/* Left Panel - Email List */}
          <EmailListPanel>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
              Inbox ({emails.length} emails)
            </h2>
            <EmailList>
              {emails.map(email => (
                <EmailItem
                  key={email.id}
                  selected={selectedEmail?.id === email.id}
                  onClick={() => handleEmailSelect(email)}
                >
                  <EmailSubject>{email.subject}</EmailSubject>
                  <EmailSender>From: {email.sender}</EmailSender>
                  <EmailDate>
                    {new Date(email.received_date).toLocaleDateString()} at{' '}
                    {new Date(email.received_date).toLocaleTimeString()}
                  </EmailDate>
                  {email.has_ai_draft && (
                    <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>
                      ✓ AI Draft Available
                    </div>
                  )}
                </EmailItem>
              ))}
              {emails.length === 0 && (
                <EmptyState>
                  No emails found. Emails will appear here when you receive them in Outlook.
                </EmptyState>
              )}
            </EmailList>
          </EmailListPanel>

          {/* Right Panel - Workflow */}
          <WorkflowPanel>
            {workflowStep === 'select-email' && (
              <WorkflowState>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📧</div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                  Select an Email
                </h3>
                <p>Choose an email from your inbox to generate an AI-powered response.</p>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '1rem' }}>
                  <strong>Note:</strong> Your Outlook signature will be automatically added to sent emails and drafts.
                </p>
              </WorkflowState>
            )}

            {workflowStep === 'draft-reply' && selectedEmail && (
              <>
                <WorkflowStep>
                  <StepTitle>
                    <StepNumber>1</StepNumber>
                    Selected Email
                  </StepTitle>
                  <EmailContent>
                    <strong>Subject:</strong> {selectedEmail.subject}<br/>
                    <strong>From:</strong> {selectedEmail.sender}<br/>
                    <strong>Date:</strong> {new Date(selectedEmail.received_date).toLocaleString()}<br/><br/>
                    {selectedEmail.body_preview || selectedEmail.body}
                  </EmailContent>
                  <Button className="primary" onClick={handleDraftReply}>
                    Draft AI Reply
                  </Button>
                </WorkflowStep>
              </>
            )}

            {workflowStep === 'select-cohort' && selectedEmail && (
              <>
                <WorkflowStep>
                  <StepTitle>
                    <StepNumber>1</StepNumber>
                    Selected Email
                  </StepTitle>
                  <EmailContent>
                    {selectedEmail.body_preview || selectedEmail.body}
                  </EmailContent>
                </WorkflowStep>

                <WorkflowStep>
                  <StepTitle>
                    <StepNumber>2</StepNumber>
                    Select Course Materials
                  </StepTitle>
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                    Choose which course materials to use for generating the response:
                  </p>
                  <CohortGrid>
                    {cohorts.map(cohort => (
                      <CohortCard
                        key={cohort.id}
                        selected={selectedCohort?.id === cohort.id}
                        onClick={() => handleCohortSelect(cohort)}
                      >
                        <CohortName>{cohort.name}</CohortName>
                        <CohortStats>
                          {cohort.content_count || 0} content items
                        </CohortStats>
                      </CohortCard>
                    ))}
                  </CohortGrid>
                  {generatingDraft && (
                    <div style={{ textAlign: 'center', color: '#6b7280' }}>
                      Generating AI draft...
                    </div>
                  )}
                </WorkflowStep>
              </>
            )}

            {workflowStep === 'review-send' && selectedEmail && (
              <>
                <WorkflowStep>
                  <StepTitle>
                    <StepNumber>1</StepNumber>
                    Original Email
                  </StepTitle>
                  <EmailContent>
                    {selectedEmail.body_preview || selectedEmail.body}
                  </EmailContent>
                </WorkflowStep>

                <WorkflowStep>
                  <StepTitle>
                    <StepNumber>2</StepNumber>
                    AI Draft Response
                  </StepTitle>
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                    Using materials from: <strong>{selectedCohort?.name}</strong>
                  </p>
                  
                  {/* Signature Status Information */}
                  {signatureStatus === 'found' && (
                    <SignatureInfo>
                      ✓ Your Outlook signature will be automatically added when sending or saving as draft.
                    </SignatureInfo>
                  )}
                  {signatureStatus === 'not-found' && (
                    <SignatureInfo className="warning">
                      ℹ️ No Outlook signature found. Your email will be sent without a signature.
                    </SignatureInfo>
                  )}
                  
                  <TextArea
                    value={aiDraft}
                    onChange={(e) => setAiDraft(e.target.value)}
                    placeholder="AI draft will appear here..."
                  />
                  <ActionButtons>
                    <Button className="primary" onClick={sendEmail}>
                      Send Email
                    </Button>
                    <Button className="outline" onClick={saveDraft}>
                      Save as Draft
                    </Button>
                    <Button className="outline" onClick={resetWorkflow}>
                      Start Over
                    </Button>
                  </ActionButtons>
                </WorkflowStep>
              </>
            )}
          </WorkflowPanel>
        </WorkflowContainer>
      </EmailPanelContainer>
    </ResponsiveLayout>
  )
}

export default EmailPanel
